import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, Directive, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatSidenav } from '@angular/material/sidenav';
import { NavigationStart, Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Breadcrumbs } from '../va-breadcrumbs/va-breadcrumbs';
import { Filters } from '../va-filter/filters/filter';

import { Observable, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, share, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';

import { AtlasDataService, AtlasLanguageService } from '@galaxy/atlas/core';
import { getLocale, setLanguage } from '@vendasta/galaxy/i18n';
import { getLocale as getDateFnsLocale } from '@vendasta/galaxy/utility/locale';
import { ActionButton } from './action-button';
import { PageInformation } from './page-information';
import { VaProductNavBadges } from './va-product-badges';
import { VaProductNavItem } from './va-product-nav-item';
import { VaProductNavService } from './va-product-nav.service';

export interface LanguageChoice {
  display: string;
  locale: string;
  flag?: string;
}

@Component({
  selector: 'va-product-nav',
  templateUrl: './va-product-nav.component.html',
  styleUrls: ['./va-product-nav.component.scss'],
  standalone: false,
})
export class VaProductNavComponent implements OnInit, OnDestroy {
  @Input()
  set navItems(items: VaProductNavItem[]) {
    this.navService.setNavItems(items);
    this.navItemsState = this.navService.navItems;
  }

  @Input() appName: string;
  @Input() slogan: string;
  @Input() brandLogoUrl: string;
  @Input() logoUrl: string;
  @Input() cobrandingLogoUrl: string;
  @Input() supportedLangs: LanguageChoice[] = [
    { display: 'English', locale: 'en', flag: '🇺🇸' },
    { display: 'Français', locale: 'fr', flag: '🇨🇦' },
  ];
  @Input() topNavEnabled = false;
  @Input() legacyTopNavEnabled = true;
  @Input() sidebarPrimaryActionButton: string;
  @Input() sidebarPrimaryActionIcon = 'add';
  @Input() upgradeText: string;
  @Input() upgradeButtonText: string;
  @Input() upgradeChipText: string;
  @Input() showGetUpgrade: boolean;
  @Input() startCollapsed = false;
  @Input() useBusinessNav = false;
  @Input() hideToolbar = false;
  @Input() useUserLanguage = false;
  @Input() disableMouseOver = false;

  @Output() sidebarPrimaryActionClicked = new EventEmitter<void>();
  @Output() sidebarUpgradeClicked = new EventEmitter<void>();
  @Output() primaryActionClicked = new EventEmitter<ActionButton>();
  @Output() secondaryActionClicked = new EventEmitter<ActionButton>();
  @Output() infoIconClicked = new EventEmitter<void>();
  @Output() navMenuExpanded = new EventEmitter<boolean>();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() scroll = new EventEmitter<Event>();
  @Output() navItemClicked = new EventEmitter<VaProductNavItem>();

  @ViewChild('sidebar', { static: true }) sidebar: MatSidenav;

  topNavHeight = 40;
  navSetExpanded = true;
  sidebarSetExpanded = true;
  mobile = false;
  mobileActionBarMargin: number;

  sidebarView: 'info' | 'filters' = 'filters';
  sidebarFilters$: Observable<Filters | null>;
  sidebarInfo$: Observable<PageInformation[] | null>;
  breadcrumbs$: Observable<Breadcrumbs[]>;
  primaryActionButton$: Observable<ActionButton | null>;
  secondaryActionButton$: Observable<ActionButton | null>;
  navBadges$: Observable<VaProductNavBadges>;
  isSidebarCollapsed$: Observable<boolean>;
  isSearchable$: Observable<boolean>;
  search$: Observable<string>;

  navItemsState: VaProductNavItem[];

  useUserLanguage$: Observable<boolean>;
  currentLang$: Observable<string>;
  showInfoIcon$: Observable<boolean>;

  topGap: number;
  searchValue = '';
  searchActive = false;
  hasPageInfo = false;

  _navViewExpanded = true;

  subscriptions: Subscription[] = [];

  onScroll($event: Event): void {
    this.navService.setScroll($event);
    $event.stopPropagation();
  }

  set navViewExpanded(expanded: boolean) {
    if (expanded !== this._navViewExpanded) {
      this.navMenuExpanded.emit(expanded);
      this._navViewExpanded = expanded;
    }
  }

  get navViewExpanded(): boolean {
    return this._navViewExpanded;
  }

  constructor(
    private navService: VaProductNavService,
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private translate: TranslateService,
    private adapter: DateAdapter<Date>,
    private atlasLanguageService: AtlasLanguageService,
    private atlasDataService: AtlasDataService,
  ) {
    this.breakpointObserver.observe(['(max-width: 995px)']).subscribe((result) => this.setMobileView(result.matches));
    this.breakpointObserver.observe(['(max-width: 995px)']).subscribe((result) => {
      if (result.matches) {
        this.navSetExpanded = this.navViewExpanded = false;
      }
    });
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      this.navService.setFilters(null);
      this.navService.setInfoSidebar(null);
      this.navService.setPrimaryActionButton(null);
      this.navService.setSecondaryActionButton(null);
      this.navService.setSearch(null);
      this.navService.setShowInfoIcon(false);
      this.sidebarView = 'filters';
      this.sidebar.close();
      if (this.mobile) {
        this.collapseNav();
      }
    });
  }

  ngOnInit(): void {
    if (this.useUserLanguage) {
      const userExists$ = this.atlasDataService.userId$.pipe(
        map((userId) => !!userId),
        startWith(false),
      );

      const isImpersonating$ = this.atlasDataService.impersonateeUsername$.pipe(
        map((impersonatingUsername) => !!impersonatingUsername),
        startWith(false),
      );

      this.useUserLanguage$ = combineLatest([userExists$, isImpersonating$]).pipe(
        map(([userExists, isImpersonating]) => userExists && !isImpersonating),
        distinctUntilChanged(),
        shareReplay(1),
      );

      this.currentLang$ = this.useUserLanguage$.pipe(
        switchMap((useUserLanguage) => {
          if (useUserLanguage) {
            return this.atlasLanguageService.language$;
          } else {
            return this.translate.onLangChange.pipe(
              map((event: LangChangeEvent) => event.lang),
              startWith(getLocale()),
            );
          }
        }),
        startWith('en'),
        distinctUntilChanged(),
      );
    } else {
      this.currentLang$ = this.translate.onLangChange.pipe(
        map((event: LangChangeEvent) => event.lang),
        startWith(this.translate.currentLang || 'en'),
      );
    }

    this.updateTopGap();
    this.mobileActionBarMargin = this.getMobileActionBarMargin();
    this.sidebarFilters$ = this.navService.filters$;
    this.breadcrumbs$ = this.navService.breadcrumbs$;
    this.sidebarInfo$ = this.navService.infoSidebar$;
    this.primaryActionButton$ = this.navService.primaryActionButton$;
    this.secondaryActionButton$ = this.navService.secondaryActionButton$;
    this.showInfoIcon$ = combineLatest([this.navService.showInfoIcon$, this.navService.infoSidebar$]).pipe(
      tap(([, pageinfo]) => (this.hasPageInfo = !!pageinfo && pageinfo.length > 0)),
      map(([flag]) => this.hasPageInfo || flag),
      share(),
    );

    this.subscriptions.push(
      this.navService.toggleNav$.subscribe(() => {
        this.toggleNavExpanded();
      }),
    );

    this.subscriptions.push(
      combineLatest([this.sidebarFilters$, this.breakpointObserver.observe([Breakpoints.Large, Breakpoints.XLarge])])
        .pipe(
          tap(([filters, result]) => {
            if (result.matches && filters && this.sidebarSetExpanded) {
              this.sidebar.open();
            }
          }),
        )
        .subscribe(),
    );

    this.isSidebarCollapsed$ = combineLatest(
      this.sidebarFilters$,
      this.sidebarInfo$,
      this.sidebar.openedChange.asObservable().pipe(startWith(this.sidebar.opened)),
    ).pipe(map(([filters, information, opened]) => this.isSidebarCollapsed(filters, information, opened)));
    this.search$ = this.navService.search$.pipe(
      distinctUntilChanged(),
      tap((search: string) => this.updateSearch(search)),
    );
    this.isSearchable$ = this.navService.search$.pipe(map((search) => search !== null));
    if (this.startCollapsed) {
      this.collapseNav();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  getMobileActionBarMargin(): number {
    if (!this.topNavEnabled && !this.legacyTopNavEnabled) {
      return this.topGap + 56;
    }
    return this.topGap + 16 + (this.topNavEnabled && !this.legacyTopNavEnabled ? this.topNavHeight : 0);
  }

  updateTopGap(): void {
    if (this.legacyTopNavEnabled || this.topNavEnabled) {
      const pageYOffset = window.pageYOffset;
      if (pageYOffset > this.topNavHeight) {
        this.topGap = 0;
      } else {
        this.topGap = this.topNavHeight - pageYOffset;
      }
    } else {
      this.topGap = 0;
    }
  }

  setMobileView(mobile: boolean): void {
    this.mobile = mobile;
    this.navSetExpanded = !mobile;
    this.navViewExpanded = !mobile;
  }

  toggleNavExpanded(): void {
    this.navSetExpanded = !this.navSetExpanded;
    this.navViewExpanded = this.navSetExpanded;
  }

  collapseNav(): void {
    this.navSetExpanded = false;
    this.navViewExpanded = false;
  }

  sidebarPrimaryAction(): void {
    if (this.mobile) {
      this.collapseNav();
    }
    this.sidebarPrimaryActionClicked.emit();
  }

  sidebarUpgradeAction(): void {
    this.sidebarUpgradeClicked.emit();
  }

  navExpanded(): boolean {
    return this.navSetExpanded ? true : this.navViewExpanded;
  }

  onLangSelection(locale: string): void {
    setLanguage(locale);
    this.adapter.setLocale(getDateFnsLocale());
    this.translate.use(locale);
    if (this.useUserLanguage$) {
      this.useUserLanguage$.pipe(take(1)).subscribe((useUserLanguage) => {
        this.atlasLanguageService.setLanguage(locale, useUserLanguage);
      });
    }
  }

  toggleSidebarInfo(): void {
    if (this.hasPageInfo) {
      if (this.sidebarView === 'info') {
        this.sidebarSetExpanded = !this.sidebar.opened;
        this.sidebar.toggle();
      } else {
        this.sidebarView = 'info';
        this.sidebar.open();
        this.sidebarSetExpanded = true;
      }
    }
    this.infoIconClicked.emit();
  }

  toggleSidebarFilters(): void {
    if (this.sidebarView === 'filters') {
      this.sidebarSetExpanded = !this.sidebar.opened;
      this.sidebar.toggle();
    } else {
      this.sidebarView = 'filters';
      this.sidebar.open();
      this.sidebarSetExpanded = true;
    }
  }

  isSidebarCollapsed(filters: Filters | null, infoSidebar: PageInformation[] | null, sidebarOpened: boolean): boolean {
    if ((!filters && !infoSidebar) || !this.sidebar) {
      return true;
    }
    return !sidebarOpened;
  }

  updateSearch(search: string): void {
    this.searchValue = search;
    this.searchActive = this.searchValue !== '';
    this.navService.setSearch(search);
  }
}

@Directive({
  selector: `va-product-nav-custom-side-content, [va-product-nav-custom-side-content], [vaProductNavCustomSideContent]`,
  standalone: false,
})
export class VaNavCustomSideContentDirective {}
